import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { clearAllUXHints } from "./UXHint";

/**
 * HelpModal — notice utilisateur légère
 * Drawer centré (modal), auto-suffisant, portal sur document.body.
 * Deux <div position:fixed> frères pour éviter le bug iOS Safari overflow-x:hidden.
 *
 * Props :
 *   onClose() — fermer la notice
 */

const SECTIONS = [
  {
    emoji: "🏪",
    title: "Référencez vos produits",
    body: "Depuis votre tableau de bord, ajoutez chaque produit via son QR code ou son code-barres. Vos produits deviennent visibles pour les clients qui les ont scannés. Le heat indicator 🔥 indique l'attractivité de chaque produit en temps réel.",
  },
  {
    emoji: "💰",
    title: "Pilotez vos infos en temps réel",
    body: "Pour chaque produit, mettez à jour le prix, le stock ou le statut (en promotion, indisponible…). Vos modifications sont prises en compte immédiatement. Les produits les plus populaires remontent automatiquement en haut de votre liste.",
  },
  {
    emoji: "🔔",
    title: "Notifiez vos clients",
    body: "Lorsqu'un client a activé une alerte sur un de vos produits, il est automatiquement notifié à chaque mise à jour. Quand des clients enregistrent un achat, vous recevez une notification groupée quotidienne — pas de spam.",
  },
  {
    emoji: "📊",
    title: "Insights produit",
    body: "Cliquez sur un produit pour voir ses stats complètes : crushes totaux, alertes actives, achats enregistrés, taux de conversion, délai moyen crush→achat, et répartition démographique de vos clients (genre, âge). Toutes les données sont anonymisées.",
    highlight: true,
  },
  {
    emoji: "🎯",
    title: "Tableau de bord enrichi",
    body: "L'onglet Aperçu affiche vos derniers scans, vos 3 produits les plus populaires, votre activité du mois et les données démographiques de vos clients. Définissez votre objectif mensuel de crushes depuis Mon Magasin — la barre de progression s'adapte automatiquement.",
  },
  {
    emoji: "💡",
    title: "Le saviez-vous ?",
    body: "CrushNow ne diffuse aucune publicité. Le service est gratuit pendant la phase de lancement. Vos clients ne voient jamais vos données commerciales — seules des statistiques anonymisées sont partagées.\nÀ venir : export PDF, rapports automatiques par mail.",
  },
];

const HelpModal = ({ onClose }) => {
  const [resetDone, setResetDone] = useState(false);

  const handleResetHints = () => {
    clearAllUXHints();
    setResetDone(true);
  };
  // ESC ferme
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Scroll lock
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const content = (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          top: 0, left: 0,
          width: "100%", height: "100%",
          zIndex: 8999,
          background: "rgba(15, 23, 42, 0.48)",
        }}
      />

      {/* Panel centré */}
      <div
        style={{
          position: "fixed",
          top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 9000,
          background: "white",
          borderRadius: "16px",
          width: "calc(100% - 32px)",
          maxWidth: "480px",
          maxHeight: "88vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 24px 60px rgba(0,0,0,0.28)",
          overflowY: "auto",
        }}
      >
        {/* En-tête */}
        <div
          style={{
            position: "sticky",
            top: 0,
            background: "white",
            zIndex: 1,
            padding: "20px 20px 14px",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 900, color: "#0f172a" }}>
              ❓ Comment ça marche ?
            </h3>
            <p style={{ margin: "3px 0 0", fontSize: "0.75rem", color: "#94a3b8" }}>
              Lecture rapide · moins de 30 secondes
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "#F1F5F9",
              border: "none",
              borderRadius: "8px",
              padding: "6px 11px",
              cursor: "pointer",
              fontSize: "1rem",
              lineHeight: 1,
              color: "#475569",
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>

        {/* Corps */}
        <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: "12px" }}>
          {SECTIONS.map((s) => (
            <div
              key={s.title}
              style={{
                padding: "14px 16px",
                borderRadius: "10px",
                background: s.highlight ? "#FFFBEB" : "#F8FAFC",
                border: s.highlight ? "1px solid #FDE68A" : "1px solid transparent",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                <span style={{ fontSize: "1.1rem" }}>{s.emoji}</span>
                <strong style={{ fontSize: "0.875rem", color: "#0f172a", fontWeight: 800 }}>
                  {s.title}
                </strong>
              </div>

              {s.body && (
                <p style={{ margin: 0, fontSize: "0.82rem", color: "#475569", lineHeight: 1.55 }}>
                  {s.body}
                </p>
              )}

              {s.items && (
                <ul style={{ margin: 0, paddingLeft: "16px", display: "flex", flexDirection: "column", gap: "3px" }}>
                  {s.items.map((item) => (
                    <li key={item} style={{ fontSize: "0.82rem", color: "#475569", lineHeight: 1.55 }}>
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        {/* Pied */}
        <div
          style={{
            position: "sticky",
            bottom: 0,
            background: "white",
            borderTop: "1px solid #e2e8f0",
            padding: "14px 20px",
            flexShrink: 0,
          }}
        >
          <button
            onClick={onClose}
            style={{
              width: "100%",
              padding: "11px",
              borderRadius: "8px",
              border: "none",
              background: "#0f172a",
              color: "white",
              fontWeight: 700,
              fontSize: "0.875rem",
              cursor: "pointer",
            }}
          >
            Compris !
          </button>
          <button
            onClick={handleResetHints}
            style={{
              width: "100%",
              marginTop: "8px",
              padding: "9px",
              borderRadius: "8px",
              border: "none",
              background: "transparent",
              color: resetDone ? "#16a34a" : "#94a3b8",
              fontWeight: 600,
              fontSize: "0.75rem",
              cursor: "pointer",
              textTransform: "none",
            }}
          >
            {resetDone ? "✅ Conseils réactivés — visibles au prochain chargement" : "↺ Réafficher tous les conseils"}
          </button>
        </div>
      </div>
    </>
  );

  return createPortal(content, document.body);
};

export default HelpModal;
